import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Text,
  Select,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Input,
  Flex,
  Textarea,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button as ChakraButton,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Tooltip,
  InputGroup,
  InputRightElement,
  IconButton,
  Spinner,
  List,
  ListItem,
  AccordionIcon,
  WrapItem,
  Wrap,
  PopoverContent,
  Popover,
  PopoverTrigger,
} from "@chakra-ui/react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from "@chakra-ui/icons";
import Button from "../../../components/ui/button";
import colors from "../../../utils/colors";
import customToast from "../../../components/Toast";
import {
  useGetDrugClasses,
  useGetDrugsByClass,
  useGetDosagesByDrugNameAndRoute,
  useGetMedicationFrequency,
  useGetMaleMedicationRoutes,
  useGetFemaleMedicationRoutes,
  useSaveTreatment,
  useGetTreatmentsByAppointmentId,
  useDeleteTreatment,
  useSearchDrugClasses,
  useSearchDrugs,
} from "../../../apis/patients";

interface TreatmentCardProps {
  type: string;
  appointmentId: string;
  patientGender?: "male" | "female";
  patientId: string;
}

interface Treatment {
  treatmentId: string | number;
  name?: string | null;
  dosage?: string | null;
  frequency?: string | null;
  duration?: string | null;
  notes?: string | null;
}

const TreatmentCard: React.FC<TreatmentCardProps> = ({
  type,
  appointmentId,
  patientGender,
  patientId,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const drugsPerPage = 5;
  const [drugNameSearchTerm, setDrugNameSearchTerm] = useState("");
  const [medicationName, setMedicationName] = useState("");
  const [customDosage, setCustomDosage] = useState("");
  const [customFrequency, setCustomFrequency] = useState("");
  const [customMedicationDuration, setCustomMedicationDuration] = useState("");
  const [selectedMedicationDuration, setSelectedMedicationDuration] =
    useState("");
  const [selectedFrequency, setSelectedFrequency] = useState("");
  const [selectedDrugClass, setSelectedDrugClass] = useState("");
  const [selectedRoute, setSelectedRoute] = useState("");
  const [selectedDrugId, setSelectedDrugId] = useState<string | null>(null);
  const [filteredDrugNames, setFilteredDrugNames] = useState<any[]>([]);
  const [selectedDosage, setSelectedDosage] = useState("");
  const [selectedDrugNotes, setSelectedDrugNotes] = useState<string | null>(
    null,
  );
  const [notes, setNotes] = useState("");

  const [medClassSearchTerm, setMedClassSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: drugClasses, isLoading: loadingDrugClasses } =
    useGetDrugClasses();
  const { data: drugNames, isLoading: loadingDrugNames } =
    useGetDrugsByClass(selectedDrugClass);
  const { data: dosages, isLoading: loadingDosages } =
    useGetDosagesByDrugNameAndRoute(medicationName, selectedRoute);

  const isFemale = patientGender?.toLowerCase() === "female";
  const { data: maleRoutes, isLoading: loadingMaleRoutes } =
    useGetMaleMedicationRoutes();
  const { data: femaleRoutes, isLoading: loadingFemaleRoutes } =
    useGetFemaleMedicationRoutes();

  const routes = isFemale ? femaleRoutes : maleRoutes;
  const loadingRoutes = isFemale ? loadingFemaleRoutes : loadingMaleRoutes;
  const { data: frequencies, isLoading: loadingFrequencies } =
    useGetMedicationFrequency();

  const [globalSearch, setGlobalSearch] = useState("");

  const { data: classSearchResults, isLoading: loadingClassSearch } =
    useSearchDrugClasses(globalSearch);

  const { data: drugSearchResults, isLoading: loadingDrugSearch } =
    useSearchDrugs(globalSearch);

  const { mutate: saveTreatment, isPending: isTreatmentPending } =
    useSaveTreatment();

  const filteredDrugClasses = useMemo(() => {
    if (isDropdownOpen && !medClassSearchTerm) {
      return Array.isArray(drugClasses) ? drugClasses : [];
    }
    if (isDropdownOpen && medClassSearchTerm) {
      const lowerCaseSearchTerm = medClassSearchTerm.toLowerCase();

      return Array.isArray(drugClasses)
        ? drugClasses.filter((drugClass: string) =>
            drugClass.toLowerCase().includes(lowerCaseSearchTerm),
          )
        : [];
    }
    return [];
  }, [medClassSearchTerm, drugClasses, isDropdownOpen]);

  const handleResetDrugClass = () => {
    setSelectedDrugClass("");
    setMedClassSearchTerm("");
    setMedicationName("");
    setSelectedRoute("");
    setDrugNameSearchTerm("");
    setFilteredDrugNames([]);
  };

  useEffect(() => {
    const drugNamesArray = Array.isArray(drugNames) ? drugNames : [];
    const filtered = drugNamesArray.filter(
      (drug: { name: string; notes?: string }) =>
        drug.name.toLowerCase().includes(drugNameSearchTerm.toLowerCase()),
    );
    setFilteredDrugNames(filtered);
    setCurrentPage(1);
  }, [drugNames, drugNameSearchTerm]);

  const totalPages = Math.ceil(filteredDrugNames.length / drugsPerPage);

  const displayedDrugNames = filteredDrugNames
    .slice((currentPage - 1) * drugsPerPage, currentPage * drugsPerPage)
    .map((drug) =>
      typeof drug === "string" ? { name: drug, notes: "" } : drug,
    );

  const generateRandomObjectId = () => {
    return (
      Math.random().toString(16).substr(2, 8) +
      Math.random().toString(16).substr(2, 8) +
      Math.random().toString(16).substr(2, 8)
    );
  };

  const handleInputFocus = () => {
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (
        containerRef.current &&
        !containerRef.current.contains(document.activeElement)
      ) {
        setIsDropdownOpen(false);

        if (!selectedDrugClass && medClassSearchTerm === "") {
          setMedClassSearchTerm("");
        } else if (
          selectedDrugClass &&
          medClassSearchTerm !== selectedDrugClass
        ) {
          setMedClassSearchTerm(selectedDrugClass);
        } else if (!selectedDrugClass && medClassSearchTerm !== "") {
        }
      }
    }, 150);
  };

  const handleIconClick = () => {
    setIsDropdownOpen((prev) => !prev);
    if (!isDropdownOpen && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSelectClass = (drugClass: string) => {
    setSelectedDrugClass(drugClass);
    setMedClassSearchTerm(drugClass);
    setIsDropdownOpen(false);

    setMedicationName("");
    setSelectedRoute("");
    setSelectedDosage("");
    setSelectedFrequency("");
    setSelectedMedicationDuration("");
    setCustomMedicationDuration("");
    setCustomDosage("");
    setCustomFrequency("");
    setDrugNameSearchTerm("");
    setSelectedDrugId(null);
  };

  const handleMedClassInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newSearchTerm = e.target.value;
    setMedClassSearchTerm(newSearchTerm);

    if (selectedDrugClass && newSearchTerm !== selectedDrugClass) {
      setSelectedDrugClass("");
    }
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  const displayValue = isDropdownOpen
    ? medClassSearchTerm
    : selectedDrugClass || medClassSearchTerm;

  const [expandedRows, setExpandedRows] = useState<Array<string | number>>([]);

  const toggleRow = (treatmentId: string | number) => {
    setExpandedRows((prevExpandedRows) => {
      if (prevExpandedRows.includes(treatmentId)) {
        return prevExpandedRows.filter((id) => id !== treatmentId);
      } else {
        return [...prevExpandedRows, treatmentId];
      }
    });
  };

  const isRowExpanded = (treatmentId: string | number) =>
    expandedRows.includes(treatmentId);

  const totalColumnCount = 5;

  const handleTreatmentSave = () => {
    if (isInvalid) {
      customToast(
        "Please fill in all medication details OR enter a note.",
        "error",
      );
      return;
    }

    let treatmentPayload: any;

    if (medicationDetailsComplete) {
      const finalDosage =
        selectedDosage === "custom" ? customDosage : selectedDosage;

      const finalFrequency =
        selectedFrequency === "custom" ? customFrequency : selectedFrequency;
      const finalDuration =
        selectedMedicationDuration === "custom"
          ? customMedicationDuration
          : selectedMedicationDuration;
      const generatedId = generateRandomObjectId();
      treatmentPayload = {
        treatmentId: generatedId,
        name: medicationName,
        route: selectedRoute,
        // dosage: selectedDosage,
        // frequency: selectedFrequency,
        dosage: finalDosage,
        frequency: finalFrequency,
        duration: finalDuration,
        notes: notes,
      };
    } else {
      treatmentPayload = {
        treatmentId: generateRandomObjectId(),
        name: "",
        route: "",
        dosage: "",
        frequency: "",
        duration: "",
        notes: notes,
      };
    }

    saveTreatment(
      {
        appointmentId,
        patientId,
        treatment: treatmentPayload,
      },
      {
        onSuccess: () => {
          customToast("Treatment information saved successfully!", "success");
          setSelectedMedicationDuration("");
          setCustomMedicationDuration("");
          setSelectedDrugClass("");
          setMedClassSearchTerm("");
          setSelectedRoute("");
          setMedicationName("");
          setDrugNameSearchTerm("");
          setSelectedFrequency("");
          setSelectedDosage("");
          setCustomDosage("");
          setCustomFrequency("");
          setSelectedDrugId(null);
          setNotes("");
        },
        onError: (error: any) => {
          customToast(
            `Error saving treatment: ${
              error.response?.data?.message || error.message
            }`,
            "error",
          );
        },
      },
    );
  };

  const {
    data: treatments,
    isLoading,
    isError,
  } = useGetTreatmentsByAppointmentId(appointmentId);
  const deleteTreatment = useDeleteTreatment();
  const [treatmentToDelete, setTreatmentToDelete] = useState<string | null>(
    null,
  );
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleDelete = () => {
    if (treatmentToDelete) {
      deleteTreatment.mutate({
        appointmentId,
        treatmentId: treatmentToDelete,
      });
      setTreatmentToDelete(null);
      onClose();
    }
  };

  const handleDeleteClick = (id: string) => {
    // console.log("Deleting treatment with id:", id);
    setTreatmentToDelete(id);
    onOpen();
  };
  const isDosageValid =
    selectedDosage &&
    (selectedDosage !== "custom" || customDosage.trim() !== "");

  const isFrequencyValid =
    selectedFrequency &&
    (selectedFrequency !== "custom" || customFrequency.trim() !== "");
  const isDurationValid =
    selectedMedicationDuration &&
    (selectedMedicationDuration !== "custom" ||
      customMedicationDuration.trim() !== "");
  const medicationDetailsComplete =
    selectedDrugClass &&
    medicationName &&
    selectedRoute &&
    // selectedDosage &&
    // selectedFrequency &&
    isDosageValid &&
    isFrequencyValid &&
    isDurationValid;
  const notesAreEmpty = notes.trim() === "";
  const isInvalid = !medicationDetailsComplete && notesAreEmpty;

  const isDisabled = !selectedDrugClass || loadingDrugNames;

  const defaultAccordionIndex = selectedDrugClass ? [0] : [];

  return (
    <Flex
      direction="column"
      backgroundColor="white"
      borderRadius="8px"
      boxShadow="1px 4px 15px 0px #3030301F"
      width="100%"
      padding="40px"
      gap="20px"
    >
      {/* GLOBAL SEARCH (NEW) */}
      <Box mb={4}>
        <Text
          fontFamily="Lato"
          fontSize="14px"
          fontWeight={600}
          color={colors.dark}
        >
          Search drug or class
        </Text>

        <Popover
          isOpen={(globalSearch || "").trim().length >= 2}
          placement="bottom-start"
          autoFocus={false}
        >
          <PopoverTrigger>
            <Input
              placeholder="Type at least 2 letters (e.g., amox, penic...)"
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              focusBorderColor={colors.primary}
              autoComplete="off"
            />
          </PopoverTrigger>

          <PopoverContent p={3} w="100%" maxW="520px">
            {loadingClassSearch || loadingDrugSearch ? (
              <Flex align="center" gap={2}>
                <Spinner size="sm" />
                <Text fontSize="sm">Searching...</Text>
              </Flex>
            ) : (
              <>
                {/* Classes */}
                <Box mb={3}>
                  <Text fontSize="sm" fontWeight={700} mb={2}>
                    Classes
                  </Text>
                  {!classSearchResults || classSearchResults.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      No matching classes
                    </Text>
                  ) : (
                    <Wrap spacing="8px">
                      {classSearchResults.map((cls: string) => (
                        <WrapItem key={cls}>
                          <ChakraButton
                            size="sm"
                            variant="outline"
                            colorScheme="blue"
                            onClick={() => {
                              handleSelectClass(cls);
                              setGlobalSearch("");
                            }}
                          >
                            {cls}
                          </ChakraButton>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>

                {/* Drugs */}
                <Box>
                  <Text fontSize="sm" fontWeight={700} mb={2}>
                    Drugs
                  </Text>
                  {!drugSearchResults || drugSearchResults.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      No matching drugs
                    </Text>
                  ) : (
                    <List spacing={2}>
                      {drugSearchResults.map(
                        (d: {
                          drugClass: string;
                          name: string;
                          notes?: string;
                        }) => (
                          <ListItem key={`${d.drugClass}::${d.name}`}>
                            <ChakraButton
                              w="100%"
                              variant="ghost"
                              justifyContent="space-between"
                              onClick={() => {
                                // Auto-select class + drug
                                setSelectedDrugClass(d.drugClass);
                                setMedClassSearchTerm(d.drugClass);
                                setIsDropdownOpen(false);

                                setMedicationName(d.name);
                                setSelectedRoute("");
                                setSelectedDosage("");
                                setSelectedFrequency("");
                                setSelectedMedicationDuration("");
                                setCustomMedicationDuration("");
                                setDrugNameSearchTerm("");
                                setGlobalSearch("");
                              }}
                            >
                              <Box textAlign="left">
                                <Text fontSize="sm" fontWeight={700}>
                                  {d.name}
                                </Text>
                                <Text fontSize="xs" color="gray.600">
                                  {d.drugClass}
                                </Text>
                              </Box>
                              <Text fontSize="xs" color="gray.500">
                                Select
                              </Text>
                            </ChakraButton>
                          </ListItem>
                        ),
                      )}
                    </List>
                  )}
                </Box>
              </>
            )}
          </PopoverContent>
        </Popover>
      </Box>

      <Flex direction="column" gap="12px">
        <Text
          fontFamily="Lato"
          fontSize="14px"
          fontWeight={600}
          color={colors.dark}
        >
          Medication class
        </Text>
        <Box mb={4} ref={containerRef} position="relative">
          <InputGroup>
            <Input
              ref={inputRef}
              placeholder={
                loadingDrugClasses
                  ? "Loading..."
                  : selectedDrugClass || "Select or search medication class"
              }
              value={displayValue}
              onChange={handleMedClassInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              pr="4.5rem"
              focusBorderColor={colors.primary}
              isDisabled={loadingDrugClasses}
              autoComplete="off"
            />
            <InputRightElement width="4.5rem">
              {loadingDrugClasses ? (
                <Spinner size="sm" />
              ) : (
                <IconButton
                  aria-label={
                    isDropdownOpen ? "Close dropdown" : "Open dropdown"
                  }
                  icon={
                    isDropdownOpen ? <ChevronUpIcon /> : <ChevronDownIcon />
                  }
                  onClick={handleIconClick}
                  variant="ghost"
                  size="sm"
                  onMouseDown={(e) => e.preventDefault()}
                  isDisabled={loadingDrugClasses}
                />
              )}
            </InputRightElement>
          </InputGroup>

          {isDropdownOpen &&
            !loadingDrugClasses &&
            (filteredDrugClasses.length > 0 ||
              (medClassSearchTerm.length > 0 &&
                filteredDrugClasses.length === 0)) && (
              <Box
                position="absolute"
                top="100%"
                left="0"
                right="0"
                zIndex={10}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                mt={1}
                bg="white"
                boxShadow="md"
                maxHeight="200px"
                overflowY="auto"
              >
                {/* Reset button outside the loop */}
                <Box p={2} textAlign="center">
                  <ChakraButton
                    onClick={handleResetDrugClass}
                    colorScheme="gray"
                    size="sm"
                    variant="outline"
                  >
                    Reset Drug Class to Default
                  </ChakraButton>
                </Box>
                {filteredDrugClasses.length === 0 && medClassSearchTerm ? (
                  <Box p={2} color="gray.500">
                    No classes found
                  </Box>
                ) : (
                  <Wrap spacing="8px" py={2}>
                    {filteredDrugClasses.map((drugClass: string) => (
                      <WrapItem key={drugClass}>
                        <Tooltip label={drugClass} hasArrow placement="top">
                          <ChakraButton
                            size="sm"
                            px={3}
                            py={2}
                            fontSize={{ base: "12px", md: "14px" }}
                            minW="120px"
                            maxW="160px"
                            h="auto"
                            whiteSpace="normal"
                            wordBreak="break-word"
                            overflowWrap="break-word"
                            textAlign="left"
                            display="inline-block"
                            variant={
                              selectedDrugClass === drugClass
                                ? "solid"
                                : "outline"
                            }
                            colorScheme="blue"
                            onClick={() => handleSelectClass(drugClass)}
                          >
                            {drugClass}
                          </ChakraButton>
                        </Tooltip>
                      </WrapItem>
                    ))}
                  </Wrap>
                )}
              </Box>
            )}

          {isDropdownOpen && loadingDrugClasses && (
            <Box
              position="absolute"
              top="100%"
              left="0"
              right="0"
              zIndex={10}
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              mt={1}
              bg="white"
              boxShadow="md"
              p={2}
              textAlign="center"
              color="gray.500"
            >
              Loading...
            </Box>
          )}
        </Box>
      </Flex>
      <Accordion
        allowToggle
        defaultIndex={defaultAccordionIndex}
        key={selectedDrugClass}
      >
        <AccordionItem border="none">
          <h2>
            <AccordionButton
              _expanded={{ bg: colors.primary + "10", color: colors.primary }}
              disabled={isDisabled}
            >
              <Box
                flex="1"
                textAlign="left"
                fontWeight={600}
                color={colors.dark}
              >
                <Box as="span" fontWeight={600} mr={1}>
                  Name:
                </Box>

                <Box as="span" fontWeight={400}>
                  {medicationName || "None selected"}
                </Box>
              </Box>

              <AccordionIcon />
            </AccordionButton>
          </h2>

          <AccordionPanel pb={4}>
            <Input
              placeholder="Search drug names..."
              value={drugNameSearchTerm}
              onChange={(e) => setDrugNameSearchTerm(e.target.value)}
              mb={4}
              focusBorderColor={colors.primary}
              isDisabled={isDisabled}
            />

            {loadingDrugNames && selectedDrugClass && (
              <Flex justifyContent="center" alignItems="center" minH="50px">
                <Spinner size="md" color={colors.primary} />
                <Text ml={2}>Loading drug names...</Text>
              </Flex>
            )}

            {!loadingDrugNames &&
              filteredDrugNames.length === 0 &&
              drugNameSearchTerm &&
              selectedDrugClass && (
                <Text color="gray.500">
                  No drug names found for "{drugNameSearchTerm}" in this class.
                </Text>
              )}
            {!loadingDrugNames &&
              filteredDrugNames.length === 0 &&
              !drugNameSearchTerm &&
              selectedDrugClass &&
              drugNames &&
              drugNames.length > 0 && (
                <Text color="gray.500">
                  No drug names available for this class.
                </Text>
              )}
            {!loadingDrugNames &&
              filteredDrugNames.length === 0 &&
              !drugNameSearchTerm &&
              selectedDrugClass &&
              (!drugNames || drugNames.length === 0) && (
                <Text color="gray.500">Fetching drug names...</Text>
              )}

            {!loadingDrugNames && displayedDrugNames.length > 0 && (
              <>
                <Box maxW="100%" overflowX="hidden" mt={4}>
                  <Wrap spacing="8px">
                    {displayedDrugNames.map(({ name, notes }) => (
                      <WrapItem>
                        <Tooltip
                          key={name}
                          label={notes || "No notes available"}
                          hasArrow
                          placement="top"
                        >
                          <Box
                            px={3}
                            py={2}
                            fontSize={{ base: "12px", md: "14px" }}
                            minW="120px"
                            maxW="160px"
                            bg="blue.50"
                            border="1px solid"
                            borderColor="blue.200"
                            borderRadius="md"
                            whiteSpace="normal"
                            wordBreak="break-word"
                            overflowWrap="break-word"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            textAlign="center"
                            cursor="pointer"
                            onClick={() => {
                              setMedicationName(name);

                              setSelectedRoute("");
                              setSelectedDosage("");
                            }}
                          >
                            {name}
                          </Box>
                        </Tooltip>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>

                {totalPages > 1 && (
                  <Flex justifyContent="space-between" alignItems="center">
                    <ChakraButton
                      size="sm"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                    >
                      Prev
                    </ChakraButton>
                    <Text fontSize="sm" alignSelf="center">
                      Page {currentPage} of {totalPages}
                    </Text>
                    <ChakraButton
                      size="sm"
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </ChakraButton>
                  </Flex>
                )}
              </>
            )}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      <Flex direction="column" gap="12px">
        <Text
          fontFamily="Lato"
          fontSize="14px"
          fontWeight={600}
          color={colors.dark}
        >
          Route
        </Text>
        <Select
          placeholder={loadingRoutes ? "Loading..." : "Select route"}
          focusBorderColor={colors.primary}
          isDisabled={!medicationName}
          value={selectedRoute}
          onChange={(e) => {
            const route = e.target.value;
            setSelectedRoute(route);
            setSelectedDosage("");
            setSelectedFrequency("");
            setSelectedMedicationDuration("");
            setCustomMedicationDuration("");
            setCustomDosage("");
            setCustomFrequency("");
          }}
        >
          {routes?.map((route) => (
            <option key={route} value={route}>
              {route}
            </option>
          ))}
        </Select>
      </Flex>
      <Flex gap="16px" alignItems="center">
        <Flex direction="column" gap="12px">
          <Text
            fontFamily="Lato"
            fontSize="14px"
            fontWeight={600}
            color={colors.dark}
          >
            Dosage
          </Text>
          <Select
            placeholder={loadingDosages ? "Loading..." : "Select dosage"}
            focusBorderColor={colors.primary}
            isDisabled={!selectedRoute}
            value={selectedDosage}
            onChange={(e) => {
              setSelectedDosage(e.target.value);
              setCustomDosage("");
              setSelectedFrequency("");
              setSelectedMedicationDuration("");
              setCustomMedicationDuration("");
              setCustomDosage("");
              setCustomFrequency("");
            }}
          >
            {dosages?.map((dosage) => (
              <option key={dosage} value={dosage} style={{ color: "black" }}>
                {dosage}
              </option>
            ))}
            <option value="custom">Custom</option>
          </Select>
          {selectedDosage === "custom" && (
            <Input
              mt="8px"
              placeholder="Enter custom dosage"
              value={customDosage}
              onChange={(e) => setCustomDosage(e.target.value)}
              focusBorderColor={colors.primary}
            />
          )}
        </Flex>
        <Flex direction="column" gap="12px">
          <Text
            fontFamily="Lato"
            fontSize="14px"
            fontWeight={600}
            color={colors.dark}
          >
            Frequency
          </Text>
          <Select
            placeholder={loadingFrequencies ? "Loading..." : "Select frequency"}
            focusBorderColor={colors.primary}
            isDisabled={!selectedDosage}
            value={selectedFrequency}
            onChange={(e) => {
              setSelectedFrequency(e.target.value);
              setCustomFrequency("");
              setSelectedMedicationDuration("");
              setCustomMedicationDuration("");
              setCustomFrequency("");
            }}
          >
            {frequencies?.map((frequency, index) => (
              <option key={`${frequency}-${index}`} value={frequency}>
                {frequency}
              </option>
            ))}
            <option value="custom">Custom</option>
          </Select>
          {selectedFrequency === "custom" && (
            <Input
              mt="8px"
              placeholder="Enter custom frequency"
              value={customFrequency}
              onChange={(e) => setCustomFrequency(e.target.value)}
              focusBorderColor={colors.primary}
            />
          )}
        </Flex>
        <Flex direction="column" gap="12px">
          <Text
            fontFamily="Lato"
            fontSize="14px"
            fontWeight={600}
            color={colors.dark}
          >
            Duration
          </Text>
          <Select
            onChange={(e) => {
              setSelectedMedicationDuration(e.target.value);
              if (e.target.value !== "custom") {
                setCustomMedicationDuration("");
              }
            }}
            focusBorderColor={colors.primary}
            isDisabled={!selectedFrequency}
            value={selectedMedicationDuration}
          >
            <option value="">Select duration</option>
            <option value="1 week">1 week</option>
            <option value="2 weeks">2 weeks</option>
            <option value="1 month">1 month</option>
            <option value="custom">Custom</option>
          </Select>
          {selectedMedicationDuration === "custom" && (
            <Input
              mt="8px"
              placeholder="Enter custom duration"
              focusBorderColor={colors.primary}
              value={customMedicationDuration}
              onChange={(e) => setCustomMedicationDuration(e.target.value)}
            />
          )}
        </Flex>
      </Flex>
      <Box mt="20px">
        {/* Desktop Table */}
        <Box display={{ base: "none", md: "block" }}>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Dosage</Th>
                <Th>Frequency</Th>
                <Th>Duration</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading && (
                <Tr>
                  <Td colSpan={totalColumnCount} textAlign="center">
                    <Flex justifyContent="center" alignItems="center">
                      <Spinner size="sm" mr={2} />
                      <Text>Loading...</Text>
                    </Flex>
                  </Td>
                </Tr>
              )}

              {isError && (
                <Tr>
                  <Td colSpan={totalColumnCount} textAlign="center">
                    <Text color="red.500">Failed to fetch history.</Text>
                  </Td>
                </Tr>
              )}

              {!isLoading && !isError && treatments?.length > 0 ? (
                treatments.map((treatment: Treatment) => (
                  <React.Fragment key={treatment.treatmentId}>
                    <Tr>
                      <Td>
                        <Flex alignItems="center">
                          {treatment.notes && (
                            <IconButton
                              aria-label={
                                isRowExpanded(treatment.treatmentId)
                                  ? "Collapse notes"
                                  : "Expand notes"
                              }
                              icon={
                                isRowExpanded(treatment.treatmentId) ? (
                                  <ChevronDownIcon />
                                ) : (
                                  <ChevronRightIcon />
                                )
                              }
                              onClick={() => toggleRow(treatment.treatmentId)}
                              size="xs"
                              variant="ghost"
                              mr={1}
                            />
                          )}
                          <Text>{treatment.name || "-"}</Text>
                        </Flex>
                      </Td>
                      <Td>{treatment.dosage || "-"}</Td>
                      <Td>{treatment.frequency || "-"}</Td>
                      <Td>{treatment.duration || "-"}</Td>
                      <Td>
                        <ChakraButton
                          colorScheme="red"
                          size="sm"
                          ml={2}
                          onClick={() =>
                            handleDeleteClick(String(treatment.treatmentId))
                          }
                        >
                          Delete
                        </ChakraButton>
                      </Td>
                    </Tr>
                    {treatment.notes &&
                      isRowExpanded(treatment.treatmentId) && (
                        <Tr>
                          <Td colSpan={totalColumnCount}>
                            <Box pl={4}>
                              <Text fontWeight="bold">Notes:</Text>
                              <Text>{treatment.notes}</Text>
                            </Box>
                          </Td>
                        </Tr>
                      )}
                  </React.Fragment>
                ))
              ) : (
                <Tr>
                  <Td colSpan={totalColumnCount} textAlign="center">
                    <Text color="gray.500">
                      No medication history recorded.
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>

        {/* Mobile View */}
        <Box display={{ base: "block", md: "none" }}>
          {isLoading && (
            <Flex justifyContent="center" alignItems="center" py={4}>
              <Spinner size="sm" mr={2} />
              <Text>Loading...</Text>
            </Flex>
          )}

          {isError && (
            <Text color="red.500" textAlign="center" py={4}>
              Failed to fetch history.
            </Text>
          )}

          {!isLoading && !isError && treatments?.length > 0 ? (
            treatments.map((treatment: Treatment) => (
              <Box
                key={treatment.treatmentId}
                borderWidth="1px"
                borderRadius="md"
                p={4}
                mb={4}
                boxShadow="sm"
              >
                <Flex justify="space-between" align="center">
                  <Text fontWeight="bold">{treatment.name || "-"}</Text>
                  {treatment.notes && (
                    <IconButton
                      aria-label={
                        isRowExpanded(treatment.treatmentId)
                          ? "Collapse notes"
                          : "Expand notes"
                      }
                      icon={
                        isRowExpanded(treatment.treatmentId) ? (
                          <ChevronDownIcon />
                        ) : (
                          <ChevronRightIcon />
                        )
                      }
                      onClick={() => toggleRow(treatment.treatmentId)}
                      size="sm"
                      variant="ghost"
                    />
                  )}
                </Flex>

                <Text>Dosage: {treatment.dosage || "-"}</Text>
                <Text>Frequency: {treatment.frequency || "-"}</Text>
                <Text>Duration: {treatment.duration || "-"}</Text>

                {treatment.notes && isRowExpanded(treatment.treatmentId) && (
                  <Box mt={2}>
                    <Text fontWeight="bold">Notes:</Text>
                    <Text>{treatment.notes}</Text>
                  </Box>
                )}

                <ChakraButton
                  colorScheme="red"
                  size="sm"
                  mt={3}
                  onClick={() =>
                    handleDeleteClick(String(treatment.treatmentId))
                  }
                >
                  Delete
                </ChakraButton>
              </Box>
            ))
          ) : (
            <Text color="gray.500" textAlign="center" py={4}>
              No medication history recorded.
            </Text>
          )}
        </Box>

        {/* AlertDialog remains unchanged */}
        <AlertDialog
          isOpen={isOpen}
          leastDestructiveRef={cancelRef}
          onClose={onClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Treatment
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to delete this treatment? This action
                cannot be undone.
              </AlertDialogBody>

              <AlertDialogFooter>
                <ChakraButton ref={cancelRef} onClick={onClose}>
                  Cancel
                </ChakraButton>
                <ChakraButton colorScheme="red" onClick={handleDelete} ml={3}>
                  Delete
                </ChakraButton>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Box>

      <Box>
        <Text
          fontFamily="Lato"
          fontSize="14px"
          fontWeight={600}
          color={colors.dark}
          mb="12px"
        >
          Note
        </Text>
        <Textarea
          placeholder="Add a note..."
          focusBorderColor={colors.primary}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Box>

      <Flex style={{ justifyContent: "flex-end" }}>
        <Button
          theme="primary"
          text="Submit"
          className="rounded-md mt-2 w-[150px]"
          onClick={handleTreatmentSave}
          disabled={isTreatmentPending || isInvalid}
        />
      </Flex>
    </Flex>
  );
};

export default TreatmentCard;
